import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonalDetails, ProfileRequestPatch } from '@unit/shared';

/**
 * The editable personal-details section, shared by the direct edit on the
 * soldier page (admin / hr / platoon commander) and the soldier's own change
 * request on /me. Both produce the same patch shape — only the fields that
 * actually changed — so the two flows cannot drift apart.
 */
export interface ProfileValues {
  phone: string | null;
  personalDetails: PersonalDetails;
}

type Flat = Record<FlatKey, string>;
type FlatKey =
  | 'phone'
  | 'dateOfBirth'
  | 'address'
  | 'bloodType'
  | 'shirt'
  | 'pants'
  | 'boots'
  | 'vest'
  | 'emergencyName'
  | 'emergencyPhone'
  | 'emergencyRelation';

export function flatten(v: ProfileValues): Flat {
  const d = v.personalDetails;
  return {
    phone: v.phone ?? '',
    dateOfBirth: d.dateOfBirth ?? '',
    address: d.address ?? '',
    bloodType: d.bloodType ?? '',
    shirt: d.sizes.shirt ?? '',
    pants: d.sizes.pants ?? '',
    boots: d.sizes.boots ?? '',
    vest: d.sizes.vest ?? '',
    emergencyName: d.emergencyContact.name ?? '',
    emergencyPhone: d.emergencyContact.phone ?? '',
    emergencyRelation: d.emergencyContact.relation ?? '',
  };
}

/** Only what changed; an empty string means "clear the field". Returns null when nothing changed. */
export function diffToPatch(before: Flat, after: Flat): ProfileRequestPatch | null {
  const val = (s: string) => (s.trim() === '' ? null : s.trim());
  const changed = (k: FlatKey) => before[k].trim() !== after[k].trim();
  const patch: ProfileRequestPatch = {};
  if (changed('phone')) patch.phone = val(after.phone);
  const pd: NonNullable<ProfileRequestPatch['personalDetails']> = {};
  for (const k of ['dateOfBirth', 'address', 'bloodType'] as const) {
    if (changed(k)) pd[k] = val(after[k]);
  }
  const sizes: NonNullable<typeof pd.sizes> = {};
  for (const k of ['shirt', 'pants', 'boots', 'vest'] as const) {
    if (changed(k)) sizes[k] = val(after[k]);
  }
  if (Object.keys(sizes).length > 0) pd.sizes = sizes;
  const contact: NonNullable<typeof pd.emergencyContact> = {};
  if (changed('emergencyName')) contact.name = val(after.emergencyName);
  if (changed('emergencyPhone')) contact.phone = val(after.emergencyPhone);
  if (changed('emergencyRelation')) contact.relation = val(after.emergencyRelation);
  if (Object.keys(contact).length > 0) pd.emergencyContact = contact;
  if (Object.keys(pd).length > 0) patch.personalDetails = pd;
  return Object.keys(patch).length > 0 ? patch : null;
}

const input =
  'w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-900';

export function PersonalDetailsForm({
  initial,
  onSubmit,
  onCancel,
  submitLabel,
  busy,
  children,
}: {
  initial: ProfileValues;
  onSubmit: (patch: ProfileRequestPatch) => void;
  onCancel: () => void;
  submitLabel: string;
  busy: boolean;
  /** Extra fields rendered above the buttons (the request's note, for instance). */
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [before] = useState(() => flatten(initial));
  const [values, setValues] = useState<Flat>(before);
  const [untouched, setUntouched] = useState(false);
  const set = (k: FlatKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUntouched(false);
    setValues((v) => ({ ...v, [k]: e.target.value }));
  };
  const field = (k: FlatKey, label: string, latin = true, type = 'text') => (
    <label className="block text-sm text-slate-700">
      {label}
      <input
        type={type}
        value={values[k]}
        onChange={set(k)}
        dir={latin ? 'ltr' : undefined}
        className={input}
        data-testid={`pd-${k}`}
      />
    </label>
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const patch = diffToPatch(before, values);
    if (!patch) {
      setUntouched(true);
      return;
    }
    onSubmit(patch);
  };

  return (
    <form onSubmit={submit} className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="personal-details-form">
      {field('phone', t('users.phone'))}
      {field('dateOfBirth', t('users.dateOfBirth'), true, 'date')}
      {field('bloodType', t('users.bloodType'))}
      {field('address', t('users.address'), false)}
      {field('shirt', t('users.sizes.shirt'))}
      {field('pants', t('users.sizes.pants'))}
      {field('boots', t('users.sizes.boots'))}
      {field('vest', t('users.sizes.vest'))}
      {field('emergencyName', t('users.emergencyName'), false)}
      {field('emergencyRelation', t('users.emergencyRelation'), false)}
      {field('emergencyPhone', t('users.emergencyPhone'))}
      {children && <div className="sm:col-span-2">{children}</div>}
      {untouched && (
        <p className="text-sm text-slate-500 sm:col-span-2" data-testid="pd-untouched">
          {t('me.nothingChanged')}
        </p>
      )}
      <div className="flex gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="pd-submit"
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="pd-cancel"
          className="rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100"
        >
          {t('users.cancel')}
        </button>
      </div>
    </form>
  );
}
